import axios from '@/api/axios-ai-setting'
import { DateTime } from 'luxon'

const state = {
  //   notification: null,
  suggestions: [],
  allSuggestions: [],
  suggestionCategories: undefined,
  suggestion: null,
  suggestionReport: null,
  suggestionDateRange: undefined,
  suggestionTotalPage: 0,
  suggestionIsLoading: false,
}

const mutations = {
  setSuggestions(state, suggestions) {
    state.suggestions = suggestions
  },
  setAllSuggestions(state, suggestions) {
    state.allSuggestions = suggestions
  },
  setSuggestion(state, suggestion) {
    state.suggestion = suggestion
  },
  setSuggestionReport(state, report) {
    state.suggestionReport = report
  },
  setSuggestionCategories(state, categories) {
    state.suggestionCategories = categories
  },
  setSuggestionDateRange(state, string) {
    state.suggestionDateRange = string
  },
  setSuggestionsTotalPage(state, pages) {
    state.suggestionTotalPage = pages
  },
  setSuggestionsIsLoading(state, status) {
    state.suggestionIsLoading = status
  },
}

const actions = {
  getSuggestions: async (
    { commit, getters },
    {
      page = 1,
      limit = 10,
      query = null,
      category = null,
      status = null,
      start_date = null,
      end_date = null,
      ranking_by = null,
    }
  ) => {
    let params = {
      page: page,
      limit: limit,
      query: query,
      category: category,
      start_date: start_date,
      end_date: end_date,
    }

    if (status) {
      params.active = status && status === 'show' ? true : false
    }
    if (ranking_by) {
      params.ranking_by = ranking_by
    }

    // Filter out properties with null values
    params = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== null)
    )

    commit('setSuggestionsIsLoading', true)
    await axios
      .get(`/ai/tracking`, {
        params: params,
      })
      .then((res) => {
        if (res.data) {
          let data = res.data.data
          data = data.map((r) => {
            return {
              ...r,
              status: r.is_active ? 'show' : 'hidden',
            }
          })
          commit('setSuggestionsTotalPage', res.data.meta.pagination.pages)
          commit('setSuggestions', data)
        } else {
          commit('setSuggestions', undefined)
        }
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        // alert(err);
        commit('setSuggestionsIsLoading', false)
      })
  },

  getAllSuggestions: async (
    { commit, getters },
    {
      page = 1,
      limit = 9999,
      query = null,
      category = null,
      status = null,
      start_date = null,
      end_date = null,
      ranking_by = null,
    }
  ) => {
    let params = {
      page: page,
      limit: limit,
      query: query,
      category: category,
      start_date: start_date,
      end_date: end_date,
    }

    if (status) {
      params.active = status && status === 'show' ? true : false
    }
    if (ranking_by) {
      params.ranking_by = ranking_by
    }

    // Filter out properties with null values
    params = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== null)
    )

    commit('setSuggestionsIsLoading', true)
    await axios
      .get(`/ai/tracking`, {
        params: params,
      })
      .then((res) => {
        if (res.data) {
          let data = res.data.data
          data = data.map((r) => {
            return {
              ...r,
              status: r.is_active ? 'show' : 'hidden',
            }
          })
          commit('setAllSuggestions', data)
        } else {
          commit('setAllSuggestions', undefined)
        }
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  getSuggestionCategories: async ({ commit, getters }) => {
    await axios
      .get(`/ai/tracking/categories`)
      .then((res) => {
        if (res.data) {
          let data = res.data
          commit('setSuggestionCategories', data)
        } else {
          commit('setSuggestionCategories', undefined)
        }
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        // alert(err);
        commit('setSuggestionsIsLoading', false)
      })
  },

  getSuggestion: async ({ commit, getters }, params) => {
    // Filter out properties with null values
    params = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== null)
    )

    await axios
      .get(`/ai/tracking/${params.id}/suggestion`, {
        params: params,
      })
      .then((res) => {
        if (res.data) {
          let data = res.data
          data.status = data.is_active ? 'show' : 'hidden'
          data.click_items = [
            {
              title: 'Premium',
              action: data.premium_clicks,
              rate: data.premium_click_rate,
              color: '#F44236',
            },
            {
              title: 'Basic',
              action: data.basic_clicks,
              rate: data.basic_click_rate,
              color: '#369EF4',
            },
            {
              title: 'Trial',
              action: data.trial_clicks,
              rate: data.trial_click_rate,
              color: '#F4D736',
            },
          ]
          data.send_items = [
            {
              title: 'Premium',
              action: data.premium_sends,
              rate: data.premium_send_rate,
              color: '#F44236',
            },
            {
              title: 'Basic',
              action: data.basic_sends,
              rate: data.basic_send_rate,
              color: '#369EF4',
            },
            {
              title: 'Trial',
              action: data.trial_sends,
              rate: data.trial_send_rate,
              color: '#F4D736',
            },
          ]
          commit('setSuggestion', data)
        } else {
          commit('setSuggestion', undefined)
        }
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  getSuggestionReport: async ({ commit, getters }, params) => {
    await axios
      .get(`/ai/tracking/report`, {
        params: params,
      })
      .then((res) => {
        if (res.data) {
          let data = res.data
          data.clicks = res.data.categories.sort(
            (a, b) => a.rank_by_click - b.rank_by_click
          )
          data.clicks = data.clicks.map((e, i) => ({
            ...e,
            rank_by_click: i + 1,
          }))
          data.sends = res.data.categories.sort(
            (a, b) => a.rank_by_send - b.rank_by_send
          )
          data.sends = data.sends.map((e, i) => ({
            ...e,
            rank_by_click: i + 1,
          }))
          commit('setSuggestionReport', data)
        } else {
          commit('setSuggestionReport', undefined)
        }
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  createSuggestion: async ({ commit, getters }, form) => {
    await axios
      .post(`/ai/tracking`, form)
      .then((res) => {
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  reorderSuggestions: async ({ commit, getters }, form) => {
    await axios
      .put(`/ai/tracking/reorder`, form)
      .then((res) => {
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  editSuggestion: async ({ commit, getters }, payload) => {
    let { id, order, ...form } = payload
    await axios
      .put(`/ai/tracking/${id}/suggestion`, form)
      .then((res) => {
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },

  deleteSuggestion: async ({ commit, getters }, id) => {
    await axios
      .delete(`/ai/tracking/${id}/suggestion`)
      .then((res) => {
        commit('setSuggestionsIsLoading', false)
      })
      .catch((err) => {
        commit('setSuggestionsIsLoading', false)
      })
  },
}

const getters = {
  suggestions(state) {
    return state.suggestions
  },
  suggestion(state) {
    return state.suggestion
  },
  allSuggestions(state) {
    return state.allSuggestions
  },
  suggestionReport(state) {
    return state.suggestionReport
  },
  suggestionCategories(state) {
    return state.suggestionCategories
  },
  suggestionTotalPage(state) {
    return state.suggestionTotalPage
  },
  suggestionIsLoading(state) {
    return state.suggestionIsLoading
  },
  suggestionDateRange(state) {
    return state.suggestionDateRange
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
